import React from 'react';
import { Users, Award, Shield, CheckCircle, Phone } from 'lucide-react';

const ATOAIPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="relative h-96 overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/vjgd6nhw_ATOAI%20%281%29.png"
          alt="ATOAI"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/56 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tight mb-4">
              <span className="text-yellow-400">ATOAI</span>
            </h1>
            <p className="text-xl text-gray-300">Adventure Tour Operators Association of India</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Users className="text-yellow-400 flex-shrink-0 mt-1" size={40} />
                <div>
                  <h2 className="text-3xl font-bold text-white mb-4">ATOAI Promotes Bungee Jumping</h2>
                </div>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  The <strong className="text-white">Adventure Tour Operators Association of India (ATOAI)</strong> is a national body that represents the adventure tourism industry in India, including bungee jumping operators. ATOAI plays a crucial role in promoting adventure tourism and setting safety standards and best practices for various adventure sports in India, including bungee jumping.
                </p>
                <p>
                  The association has developed detailed guidelines for adventure tourism that are based on international best practices. These cover essential aspects like safety procedures, equipment standards, staff qualifications, and risk management systems — ensuring that bungee jumping in India is conducted responsibly and safely.
                </p>
              </div>
            </section>

            {/* ATOAI Guidelines */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Shield className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <h3 className="text-2xl font-bold text-white">Safety Standards & Guidelines</h3>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  By encouraging professional training and certified equipment use, ATOAI ensures that bungee jumping experiences across India — in locations like Goa, Coorg, Rishikesh, and Delhi — meet global safety benchmarks. This helps establish India's best bungee jumping destinations as trusted adventure hubs.
                </p>
                <p>
                  ATOAI also supports the growth of extreme adventure sports in India by guiding operators to maintain high safety standards, proper maintenance of equipment, and experienced jump masters. This guarantees a safe yet thrilling bungee jumping experience for every adventurer.
                </p>
              </div>
            </section>

            {/* Key Aspects */}
            <section className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">ATOAI Coverage Areas</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-bold mb-3">Safety Procedures</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Pre-jump safety briefings</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Medical screening protocols</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Emergency response plans</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-bold mb-3">Equipment Standards</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Certified equipment only</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Regular maintenance schedules</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Quality assurance checks</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-bold mb-3">Staff Qualifications</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Certified jump masters</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Professional training programs</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Ongoing skill development</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-bold mb-3">Risk Management</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Comprehensive risk assessments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Insurance coverage</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Incident reporting systems</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Recommendation */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Choosing ATOAI Members</h3>
              <p className="text-gray-300 leading-relaxed">
                Individuals interested in bungee jumping in India are encouraged to choose ATOAI-member operators, as these organizations strictly adhere to government and association safety protocols.
              </p>
            </section>

            {/* Indya Bungy Membership */}
            <section className="bg-gradient-to-br from-green-500/10 to-green-900/10 border border-green-500/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Award className="text-green-400 flex-shrink-0 mt-1" size={32} />
                <h3 className="text-2xl font-bold text-white">Indya Bungy - Proud ATOAI Member</h3>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Goa Bungy, operated by Indya Bungy, is a registered and reputed bungee jumping operator in Goa, recognized by the Ministry of Tourism (MOT), Government of Goa, and Goa Tourism, and <strong className="text-white">proudly an allied member of ATOAI</strong> (Adventure Tour Operators Association of India).
                </p>
                <p>
                  We are dedicated to providing the safest and most thrilling bungee jumping experience in India, following international safety guidelines and using certified equipment. Whether you're seeking the highest bungee jump in India or a fun and safe first-time jump, Indya Bungy ensures world-class safety, expert supervision, and unforgettable adventure.
                </p>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center">
              <a
                href="tel:8506865393"
                className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
              >
                <Phone size={24} />
                <span>Call Us: 8506865393</span>
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ATOAIPage;
